import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { rdcMasterAPI, masterDataAPI } from "../services/api";
import Swal from "sweetalert2";
import $ from "jquery";
import "select2";
import "select2/dist/css/select2.min.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "./RaiseComplaint.css";
import "./AddEditTerritoryMaster.css";
import { getRoutePermissions } from "../utils/auth";

const normalizeRegion = (item) => ({
  regionCode: item.regionCode || item.RegionCode || item.REGIONCODE || "",
  regionName: item.regionName || item.RegionName || item.REGIONNAME || "",
});

const normalizeRdc = (item) => ({
  rdcCode: item.rdcCode || item.RdcCode || item.RDCCODE || "",
  rdcId: item.rdcId || item.RdcId || item.RDCID || "",
  rdcName: item.rdcName || item.RdcName || item.RDCNAME || "",
  regionCode: item.regionCode || item.RegionCode || item.REGIONCODE || "",
  isActive: item.isActive ?? item.IsActive ?? true,
});

const AddEditRDCMaster = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { rdcCode } = useParams();
  const isEditMode = !!rdcCode;

  const [rdc, setRdc] = useState({
    rdcCode: "",
    rdcId: "",
    rdcName: "",
    regionCode: "",
    isActive: true,
  });

  const [dropdowns, setDropdowns] = useState({
    regions: [],
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const regionSelectRef = useRef(null);

  const currentUser = (() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();

  // Get permissions for the parent route (/rdcs)
  const parentPath = "/rdcs";
  const routePermissions = getRoutePermissions(parentPath);
  const { canAdd, canEdit } = routePermissions;

  useEffect(() => {
    // Check permissions based on mode
    if (isEditMode && !canEdit) {
      Swal.fire({
        title: "Access Denied",
        text: "You do not have permission to edit RDCs.",
        icon: "error",
        confirmButtonText: "OK",
      }).then(() => {
        navigate("/rdcs");
      });
      return;
    } else if (!isEditMode && !canAdd) {
      Swal.fire({
        title: "Access Denied",
        text: "You do not have permission to add RDCs.",
        icon: "error",
        confirmButtonText: "OK",
      }).then(() => {
        navigate("/rdcs");
      });
      return;
    }

    loadDropdowns();
    if (isEditMode) {
      loadRdc();
    } else {
      loadNextRdcCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rdcCode, isEditMode, canAdd, canEdit]);

  // Initialize Select2 for Region dropdown
  useEffect(() => {
    if (dropdowns.regions.length === 0) {
      return;
    }

    if (regionSelectRef.current) {
      const $regionSelect = $(regionSelectRef.current);
      if ($regionSelect.data("select2")) {
        $regionSelect.select2("destroy");
      }
      $regionSelect.select2({
        width: "100%",
        placeholder: "Select Region",
        allowClear: false,
      });
      $regionSelect.on("select2:select", function (e) {
        handleChange("regionCode", e.params.data.id);
      });
      if (rdc.regionCode) {
        $regionSelect.val(rdc.regionCode).trigger("change");
      }
    }

    return () => {
      if (regionSelectRef.current) {
        const $select = $(regionSelectRef.current);
        if ($select.data("select2")) {
          $select.off("select2:select");
          $select.select2("destroy");
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdowns.regions]);

  // Update Select2 values when rdc changes
  useEffect(() => {
    if (regionSelectRef.current && dropdowns.regions.length > 0) {
      const $regionSelect = $(regionSelectRef.current);
      if ($regionSelect.data("select2")) {
        const currentVal = $regionSelect.val();
        if (currentVal !== (rdc.regionCode || "")) {
          $regionSelect.val(rdc.regionCode || "").trigger("change");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rdc.regionCode]);

  const loadDropdowns = async () => {
    try {
      setLoading(true);
      const regionRes = await masterDataAPI.getRegions();

      setDropdowns({
        regions: (regionRes?.data || []).map((r) => normalizeRegion(r)),
      });
    } catch (e) {
      console.error("Error loading dropdowns:", e);
      setError("Failed to load dropdown data. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  const loadNextRdcCode = async () => {
    try {
      const response = await rdcMasterAPI.getNextCode();
      if (response && response.success && response.data) {
        setRdc((prev) => ({ ...prev, rdcCode: response.data }));
      }
    } catch (e) {
      console.error("Error loading next RDC code:", e);
      setRdc((prev) => ({ ...prev, rdcCode: "RDC00001" }));
    }
  };

  const loadRdc = async () => {
    try {
      setLoading(true);
      const response = await rdcMasterAPI.getByCode(rdcCode);
      if (response && response.success && response.data) {
        const rdcData = normalizeRdc(response.data);
        setRdc(rdcData);
      } else {
        navigate("/rdcs");
      }
    } catch (e) {
      console.error("Error loading RDC:", e);
      if (e.response?.status !== 404) {
        Swal.fire("Error", "Failed to load RDC details", "error");
      }
      navigate("/rdcs");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setRdc((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!rdc.rdcCode?.trim()) {
      Swal.fire("Error", "RDC Code is required", "error");
      return false;
    }
    if (!rdc.rdcName?.trim()) {
      Swal.fire("Error", "RDC Name is required", "error");
      return false;
    }
    if (!rdc.regionCode) {
      Swal.fire("Error", "Region is required", "error");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const createdBy = currentUser?.userIdCode || "SYSADMIN001";
      let response;

      if (isEditMode) {
        response = await rdcMasterAPI.update(rdcCode, rdc, createdBy);
      } else {
        response = await rdcMasterAPI.create(rdc, createdBy);
      }

      if (response && response.success) {
        Swal.fire({
          title: "Success",
          text: response.message || "RDC saved successfully",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          navigate("/rdcs");
        });
      } else {
        Swal.fire(
          "Error",
          response.message || "Failed to save RDC",
          "error"
        );
      }
    } catch (e) {
      console.error("Error saving RDC:", e);
      Swal.fire(
        "Error",
        e.response?.data?.message ||
          "Failed to save RDC. Please try again.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box className="add-edit-product-loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box className="add-edit-product-header">
        <Breadcrumbs
          aria-label="breadcrumb"
          className="add-edit-product-breadcrumb"
        >
          <Link
            underline="hover"
            color="inherit"
            href="/rdcs"
            onClick={(e) => {
              e.preventDefault();
              navigate("/rdcs");
            }}
            className="breadcrumb-link brdcrmbLstitms"
          >
            RDC Master
          </Link>
          <Typography
            color="text.primary"
            className="breadcrumb-current brdcrmbLstitms brdcrmbCurPg"
          >
            {isEditMode ? "Edit RDC" : "Add RDC"}
          </Typography>
        </Breadcrumbs>
        <Box className="add-edit-product-header-right">
          <Button
            className="add-edit-product-back-button"
            onClick={() => navigate("/rdcs")}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          className="add-edit-product-error-alert"
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <div className="container-fluid add-edit-product-page">
        {/* Basic Information Section */}
        <div className="card-panel mb-3">
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-6 col-lg-4">
              <div className="select2-wrapper">
                <label className="select2-label pnlLblsze">
                  Region <span style={{ color: "red" }}>*</span>
                </label>
                <select
                  ref={regionSelectRef}
                  className="form-control select2-region"
                  required
                >
                  <option value="">Select Region</option>
                  {dropdowns.regions.map((r) => (
                    <option key={r.regionCode} value={r.regionCode}>
                      {r.regionName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4" style={{ display: "none" }}>
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  RDC Code
                </label>
                <TextField
                  value={rdc.rdcCode}
                  fullWidth
                  disabled={true}
                  size="small"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  RDC ID
                </label>
                <TextField
                  value={rdc.rdcId || ""}
                  onChange={(e) => handleChange("rdcId", e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Enter RDC ID"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  RDC Name <span style={{ color: "red" }}>*</span>
                </label>
                <TextField
                  value={rdc.rdcName}
                  onChange={(e) => handleChange("rdcName", e.target.value)}
                  required
                  fullWidth
                  size="small"
                  placeholder="Enter RDC name"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="row mt-4">
          <div className="col-12">
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={() => navigate("/rdcs")}
                className="btn-cancel-product funbtns"
                startIcon={<CancelIcon />}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                startIcon={
                  saving ? <CircularProgress size={20} /> : <SaveIcon />
                }
                className="btn-save-product funbtns"
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </Stack>
          </div>
        </div>
      </div>
    </Box>
  );
};

export default AddEditRDCMaster;
